import { FC, useEffect } from "react";
import { Control, useFieldArray, UseFormSetValue } from "react-hook-form";
import { Info, Delete } from "@mui/icons-material";
import {
  Box,
  Typography,
  Grid2,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
} from "@mui/material";
import { ProviderTypes } from "../../types/identityProvider";
import FormAutocomplete from "../forms/FormAutocomplete";
import FormTextField from "../forms/FormTextField";
import type { RoleAssignmentMode, SettingsFormValues } from "./SettingsForm";
import { useSkeletonLoader } from "../../hooks/useSkeletonLoader";
import { useDispatch, useSelector } from "../../store";
import Button from "../base/Button";
import { getRoles } from "../../slices/user";
import extractEmailDomain from "../../helpers/extractEmailDomain";
import checkRole, { UserRoles } from "../../helpers/checkUserRoles";

const ROLE_CLAIM_ATTR_KEY = "custom:idp_roles" as const;

const STANDARD_ATTR_KEYS = [
  "email",
  "email_verified",
  "username",
  "family_name",
  "given_name",
] as const;

const formatAttributeLabel = (key: string): string => {
  const normalized = key
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  return (
    normalized
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ") || "Attribute"
  );
};

interface Props {
  control: Control<SettingsFormValues, any>;
  formValues: SettingsFormValues;
  setValue: UseFormSetValue<SettingsFormValues>;
  roleMode: RoleAssignmentMode;
  onRoleModeChange: (mode: RoleAssignmentMode) => void;
}

const SsoForm: FC<Props> = (props) => {
  const { control, formValues, setValue, roleMode, onRoleModeChange } = props;

  const dispatch = useDispatch();

  const { status } = useSelector((store) => store.company);
  const { userRoles } = useSelector((store) => store.user);
  const { dbUser } = useSelector((store) => store.auth);

  const {
    fields: roleMappingFields,
    append: appendRoleMappingField,
    remove: removeRoleMappingField,
  } = useFieldArray({
    control,
    name: "ssoSettings.roleMappingsList",
  });

  const addRoleMapping = () =>
    appendRoleMappingField({
      matchValue: "",
      role: undefined,
      roleId: undefined,
    });

  const { loadWithSkeleton } = useSkeletonLoader(status === "loading");

  useEffect(() => {
    if (roleMode !== "mapped") return;
    if (roleMappingFields.length > 0) return;
    appendRoleMappingField({
      matchValue: "",
      role: undefined,
      roleId: undefined,
    });
  }, [appendRoleMappingField, roleMappingFields.length, roleMode]);

  useEffect(() => {
    if (!userRoles?.length) return;
    const mappings = formValues.ssoSettings?.roleMappingsList;
    if (!mappings?.length) return;

    mappings.forEach((mapping, index) => {
      const desiredId = mapping?.role?.id ?? mapping?.roleId;
      if (!desiredId) return;
      const matchedRole = userRoles.find((role) => role.id === desiredId);
      if (!matchedRole) return;
      if (mapping?.role?.id === matchedRole.id) return;
      setValue(`ssoSettings.roleMappingsList.${index}.role`, matchedRole, {
        shouldDirty: false,
      });
    });
  }, [formValues?.ssoSettings?.roleMappingsList, setValue, userRoles]);

  useEffect(() => {
    setValue("ssoSettings.providerType", {
      id: ProviderTypes.OIDC,
      value: "OIDC",
    });
  }, [formValues.useSso, formValues.ssoSettings?.mappedAttributes, setValue]);

  useEffect(() => {
    if (formValues.useSso && formValues.contactEmail) {
      const base = extractEmailDomain(formValues.contactEmail);
      if (base) setValue("ssoSettings.providerName", base);
    }
  }, [formValues.contactEmail, formValues.useSso, setValue]);

  useEffect(() => {
    if (!checkRole(dbUser!, UserRoles.AnyAdmin, true)) return;

    dispatch(getRoles());
  }, [dbUser, dispatch]);

  return (
    <>
      <Box display="flex" flexDirection="row" width="100%" marginY={1}>
        <Typography fontWeight="bold">SSO Setup</Typography>
      </Box>
      <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
        {loadWithSkeleton(
          <FormTextField
            label="Provider Name"
            control={control}
            fieldName="ssoSettings.providerName"
            disabled
          />
        )}
      </Grid2>
      {formValues?.ssoSettings?.providerType?.id === ProviderTypes.OIDC && (
        <>
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            {loadWithSkeleton(
              <FormTextField
                label="Issuer URL"
                control={control}
                fieldName="ssoSettings.issuerUrl"
                required
              />
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            {loadWithSkeleton(
              <FormTextField
                label="Logout URL"
                control={control}
                fieldName="ssoSettings.logoutUrl"
                helperText="Base logout URL for your IDP without parameters"
              />
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            {loadWithSkeleton(
              <FormTextField
                label="Client ID"
                control={control}
                fieldName="ssoSettings.clientId"
                required
              />
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            {loadWithSkeleton(
              <FormTextField
                label="Client Secret"
                control={control}
                fieldName="ssoSettings.clientSecret"
                type="password"
              />
            )}
          </Grid2>
        </>
      )}
      {formValues?.ssoSettings?.providerType?.id === ProviderTypes.SAML && (
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          {loadWithSkeleton(
            <FormTextField
              label="Metadata URL"
              control={control}
              fieldName="ssoSettings.metadataUrl"
            />
          )}
        </Grid2>
      )}
      <Box display="flex" flexDirection="row" width="100%" marginY={1}>
        <Typography fontWeight="bold">New User Role Assignment</Typography>
      </Box>
      <Grid2 size={{ xs: 12 }}>
        <FormControl>
          <RadioGroup
            row
            value={roleMode}
            onChange={(event) =>
              onRoleModeChange(event.target.value as RoleAssignmentMode)
            }
          >
            <FormControlLabel
              value="default"
              control={<Radio />}
              label="Set Default Roles"
            />
            <FormControlLabel
              value="mapped"
              control={<Radio />}
              label="Map Roles"
            />
          </RadioGroup>
        </FormControl>
      </Grid2>
      {roleMode === "default" && (
        <>
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            {loadWithSkeleton(
              <FormAutocomplete
                label="Select Role"
                control={control}
                fieldName="ssoSettings.defaultRoles"
                options={userRoles || []}
                multiple
              />
            )}
          </Grid2>
          <Grid2
            size={{ xs: 12, sm: 6, md: 8 }}
            display="flex"
            flexDirection="row"
            gap={1}
            alignItems="center"
            paddingX={4}
          >
            <Info color="primary" />
            <Typography variant="caption" color="primary">
              Select default roles for new domain users. If none are chosen,
              access will be limited until roles are assigned. Manage roles
              anytime on the Users page. Only roles for active products will
              appear here.
            </Typography>
          </Grid2>
        </>
      )}
      {roleMode === "mapped" && (
        <>
          {roleMappingFields.map((field, index) => (
            <Grid2 key={field.id} size={{ xs: 12 }}>
              <Box display="grid" gridTemplateColumns="2fr 2fr auto" gap={1}>
                <FormTextField
                  label="Incoming IDP Role Value"
                  control={control}
                  required
                  fieldName={`ssoSettings.roleMappingsList.${index}.matchValue`}
                />
                <FormAutocomplete
                  label="SecureVizual Role"
                  control={control}
                  fieldName={`ssoSettings.roleMappingsList.${index}.role`}
                  options={userRoles || []}
                  required
                  onChange={(option) =>
                    setValue(
                      `ssoSettings.roleMappingsList.${index}.roleId`,
                      option?.id,
                      { shouldDirty: true }
                    )
                  }
                />
                <IconButton
                  aria-label="remove"
                  onClick={() => removeRoleMappingField(index)}
                  size="small"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </Grid2>
          ))}
          <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
            <Button
              type="button"
              variant="outlined"
              onClick={addRoleMapping}
              label="Add Mapping"
            />
          </Grid2>
          <Grid2
            size={{ xs: 12, sm: 6, md: 10 }}
            display="flex"
            flexDirection="row"
            gap={1}
            alignItems="center"
            paddingX={4}
          >
            <Info color="primary" />
            <Typography variant="caption" color="primary">
              Map incoming IDP role or group values to SecureVizual roles so
              users automatically receive matching access when they sign in.
            </Typography>
          </Grid2>
        </>
      )}
      <Box display="flex" flexDirection="row" width="100%" marginY={1}>
        <Typography fontWeight="bold">Mapped Attributes</Typography>
      </Box>
      {STANDARD_ATTR_KEYS.map((key) => (
        <Grid2 key={key} size={{ xs: 12, sm: 6, md: 4 }}>
          {loadWithSkeleton(
            <FormTextField
              label={formatAttributeLabel(key)}
              control={control}
              fieldName={`ssoSettings.mappedAttributes.${key}`}
              required
            />
          )}
        </Grid2>
      ))}
      {roleMode === "mapped" && (
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          {loadWithSkeleton(
            <FormTextField
              label="IdP Role Claim Name"
              helperText="Enter the claim in your IdP token that lists the user's roles/groups."
              control={control}
              fieldName={`ssoSettings.mappedAttributes.${ROLE_CLAIM_ATTR_KEY}`}
              required
            />
          )}
        </Grid2>
      )}
      <Box display="flex" flexDirection="row" width="100%" marginY={1}>
        <Typography fontWeight="bold">
          Provide the following information to your identity provider as needed:
        </Typography>
      </Box>
      <Grid2 size={{ xs: 6 }} justifyContent="center">
        <Typography variant="caption" color="primary">
          <strong>Application Login URI:</strong>
          <br />
          {`https://${process.env.REACT_APP_COGNITO_DOMAIN}/oauth2/authorize`}
        </Typography>
      </Grid2>
      <Grid2 size={{ xs: 6 }} justifyContent="center">
        <Typography variant="caption" color="primary">
          <strong>Callback URL:</strong>
          <br />
          {`https://${process.env.REACT_APP_COGNITO_DOMAIN}/oauth2/idpresponse`}
        </Typography>
      </Grid2>
      <Grid2 size={{ xs: 12 }} justifyContent="center">
        <Typography variant="caption" color="primary">
          <strong>Logout URL:</strong>
          <br />
          {`https://${process.env.REACT_APP_COGNITO_DOMAIN}/logout`}
        </Typography>
      </Grid2>
    </>
  );
};

export default SsoForm;
